// /home/szymon/kreator2/frontend/src/components/editor/EditorState.js
// v10.1.0 – Dodano loadingPages + usunięto zbędne logi.

import { useState, useEffect, useRef } from "react";
import { saveProjectToDb } from "../../services/projectApi";
// Importujemy nową funkcję pomocniczą
import { calculateCenteredX } from "./snapUtils";

// Usunięto funkcję autoSnapAllPagesWithSimulation - wraca do Editor.js


// Uniwersalna funkcja do zapisu w DB (admin / client).
async function handleSaveToDb({
  projectId,
  downloadKey,
  jsonData,
  saveType,
  backendUrl,
  isPublicMode,
  publicMethodUsed, // "key" / "id" / null => decyduje ścieżkę w trybie public
  isTemplateEditor, // Dodano flagę isTemplateEditor
  options = {} // Dodano parametr options dla dodatkowych opcji
}) {
  // Minimalny log, do debug ewentualnego
  console.log("[EditorState] handleSaveToDb =>", { 
    projectId, 
    downloadKey, 
    saveType, 
    isTemplateEditor, 
    isPublicMode,
    autoCentered: options.autoCentered // Dodano log dla autoCentered
  });

  let url;
  if (!isPublicMode) {
    // Tryb admin: rozróżnij zapis szablonu od projektu
    if (isTemplateEditor) {
      url = `${backendUrl}/admin/templates/${projectId}/history`;
      console.log(`[EditorState] handleSaveToDb: Using TEMPLATE save URL: ${url}`);
    } else {
      url = `${backendUrl}/admin/projects/${projectId}/history`;
      console.log(`[EditorState] handleSaveToDb: Using PROJECT save URL: ${url}`);
    }
  } else {
    // Tryb public (zawsze dotyczy projektów, nie szablonów)
    if (publicMethodUsed === "key") {
      url = `${backendUrl}/client/projects-key/${downloadKey}/history`;
    } else {
      url = `${backendUrl}/client/projects/${projectId}/history`;
    }
  }

  // Dodajemy flagę autoCentered do danych zapisu, jeśli została przekazana
  const saveData = {
    url,
    jsonData,
    saveType,
    autoCentered: options.autoCentered || false // Domyślnie false, jeśli nie podano
  };

  return saveProjectToDb(saveData);
}

// Funkcja pomocnicza do konwersji lokalnej ścieżki na URL
function convertLocalPathToUrl(localPath, backendUrl, isPublicMode) {
  if (!localPath || typeof localPath !== 'string') {
    return localPath; // Zwróć oryginalną wartość, jeśli nie jest to poprawna ścieżka
  }
  const basePath = "/home/szymon/kreator2/backend/uploads/";
  let relativePath = localPath;
  if (localPath.startsWith(basePath)) {
    relativePath = localPath.substring(basePath.length);
  }
  // Usuń potencjalne znaki specjalne z początku ścieżki, jeśli nie jest to część 'images/' lub 'fonts/'
  if (!relativePath.startsWith('images/') && !relativePath.startsWith('fonts/')) {
      relativePath = relativePath.replace(/^[^a-zA-Z0-9]+/, '');
  }

  const prefixRaw = isPublicMode
    ? `${backendUrl}/client/raw-file?path=`
    : `${backendUrl}/admin/raw-file?path=`;

  // Upewnij się, że backendUrl nie ma końcowego slasha, a ścieżka nie ma początkowego
  const cleanBackendUrl = backendUrl.endsWith('/') ? backendUrl.slice(0, -1) : backendUrl;
  const cleanRelativePath = relativePath.startsWith('/') ? relativePath.slice(1) : relativePath;

  const rawFileEndpoint = isPublicMode ? '/client/raw-file' : '/admin/raw-file';

  // Zwróć pełny URL
  // Przykład: http://localhost:6001/admin/raw-file?path=images%2Fmy-image.svg
  return `${cleanBackendUrl}${rawFileEndpoint}?path=${encodeURIComponent(cleanRelativePath)}`;
}


/**
 * parseOnePage:
 *  - baseWidth/baseHeight -> fallback: pageWidth/pageHeight -> fallback: 800×600
 */
function parseOnePage(pageData) {
  let w = pageData.baseWidth;
  let h = pageData.baseHeight;

  if (typeof w !== "number" || typeof h !== "number") {
    w = pageData.pageWidth;
    h = pageData.pageHeight;
  }
  if (typeof w !== "number" || isNaN(w)) w = 800;
  if (typeof h !== "number" || isNaN(h)) h = 600;

  // Process items array if it exists (for templates)
  const texts = [];
  const images = [];
  const svgs = [];
  if (Array.isArray(pageData.items)) {
    pageData.items.forEach(item => {
      // Convert ID to string for Konva compatibility
      // Extract x, y from bbox
      const x = item.bbox ? item.bbox[0] : 0;
      const y = item.bbox ? item.bbox[1] : 0;
      // --- ADD LOGGING ---
      console.log(`[parseOnePage] Item ID: ${item.id}, Type: ${item.type}, BBox: ${JSON.stringify(item.bbox)}, Extracted X: ${x}, Extracted Y: ${y}`);
      // --- END LOGGING ---
      const stringIdItem = { ...item, id: String(item.id), x: x, y: y };

      if (item.type === 'text') {
        texts.push(stringIdItem);
      } else if (item.type === 'image') {
        // Ensure imagePath is converted to src if necessary
        // Use width/height from item if available, otherwise calculate from bbox (less common for images here)
        const width = item.width || (item.bbox ? item.bbox[2] - item.bbox[0] : undefined);
        const height = item.height || (item.bbox ? item.bbox[3] - item.bbox[1] : undefined);
        images.push({ ...stringIdItem, src: item.imagePath || item.src, width, height });
      } else if (item.type === 'vectorSVG') { // Poprawiono warunek na 'vectorSVG'
         const width = item.width || (item.bbox ? item.bbox[2] - item.bbox[0] : undefined);
         const height = item.height || (item.bbox ? item.bbox[3] - item.bbox[1] : undefined);
        svgs.push({ ...stringIdItem, src: item.svgPath || item.src, width, height }); // Używamy svgPath jako źródła
      }
    });
  } else {
    // Fallback to direct arrays (for projects/orders) - ensure IDs are strings and x/y are present
    (pageData.texts || []).forEach(item => texts.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
    (pageData.images || []).forEach(item => images.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
    (pageData.svgs || []).forEach(item => svgs.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
  }
  // Removed duplicated fallback block and extra closing brace

  return {
    baseWidth: w,
    baseHeight: h,
    texts: texts,
    images: images,
    svgs: svgs,
    guides: pageData.guides || [],
    personalizacja: pageData.personalizacja || [],
    pageIndex: pageData.pageIndex ?? 0
  };
}

export function useEditorState({
  backendUrl = "http://localhost:6001",
  pdfData = null,
  projectId = null, // Może być ID projektu lub ID pliku szablonu
  downloadKey = null,
  isAccepted = false,
  isTemplateEditor = false // Nowa flaga wskazująca, czy edytujemy szablon
}) {
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [publicMode, setPublicMode] = useState(false);
  const [publicMethodUsed, setPublicMethodUsed] = useState(null); // "key"|"id"|null

  const [fontsAreLoaded, setFontsAreLoaded] = useState(false);
  const [availableFonts, setAvailableFonts] = useState([]);

  const [assetsAreLoaded, setAssetsAreLoaded] = useState(false);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsCount, setAssetsCount] = useState(0);

  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [panelMode, setPanelMode] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);

  const [forceRefresh, setForceRefresh] = useState(0);
  const changesCountRef = useRef(0);

  const [hasLoadedFromDb, setHasLoadedFromDb] = useState(false); // Czy dane pochodzą z DB?
  const [loadingPages, setLoadingPages] = useState(true); // Start loading initially
  const [initialAutoCentered, setInitialAutoCentered] = useState(false); // Nowy stan
  // Usunięto initialSetupDone - nie jest już potrzebny

  const initialCenteringAppliedRef = useRef(false); // Nowy ref do śledzenia centrowania
  const initialLoadEffectRan = useRef(false); // Ref do śledzenia, czy główny efekt ładowania już się wykonał

  // ============ Auto-zapis =============
  function doAutoSave(customPages) {
    if (publicMode && isAccepted) return; // brak zapisu w public mode po akceptacji

    const usedPages = customPages || pages;
    // Głęboka kopia, aby nie modyfikować oryginalnego stanu `pages`
    const pagesToSave = JSON.parse(JSON.stringify(usedPages));

    // Konwertuj ścieżki SVG na URL przed zapisem
    pagesToSave.forEach(page => {
      if (page.svgs && Array.isArray(page.svgs)) {
        page.svgs = page.svgs.map(svg => {
          // Sprawdź, czy src wygląda jak lokalna ścieżka przed konwersją
          if (svg.src && typeof svg.src === 'string' && svg.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
             console.log(`[EditorState doAutoSave] Converting SVG path to URL: ${svg.src}`);
             const urlSrc = convertLocalPathToUrl(svg.src, backendUrl, publicMode);
             console.log(`[EditorState doAutoSave] Converted URL: ${urlSrc}`);
             return { ...svg, src: urlSrc };
          }
          return svg; // Zwróć bez zmian, jeśli src nie jest lokalną ścieżką
        });
      }
       // Podobnie dla obrazków, jeśli używają lokalnych ścieżek
       if (page.images && Array.isArray(page.images)) {
        page.images = page.images.map(img => {
          if (img.src && typeof img.src === 'string' && img.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
            console.log(`[EditorState doAutoSave] Converting Image path to URL: ${img.src}`);
            const urlSrc = convertLocalPathToUrl(img.src, backendUrl, publicMode);
            console.log(`[EditorState doAutoSave] Converted URL: ${urlSrc}`);
            return { ...img, src: urlSrc };
          }
          return img;
        });
      }
    });


    const final = pagesToSave.map((pg, idx) => ({
      ...pg,
      pageIndex: pg.pageIndex ?? idx
    }));
    const finalJson = { pages: final };
    if (downloadKey) finalJson.downloadKey = downloadKey;

    handleSaveToDb({
      projectId,
      downloadKey,
      jsonData: finalJson,
      saveType: "autosave",
      backendUrl,
      isPublicMode: publicMode,
      publicMethodUsed,
      isTemplateEditor // Przekaż flagę
    }).catch((err) => { console.error("Autosave failed:", err); });
    // *** NOWY LOG: Sprawdź zawartość svgs przed zapisem ***
    console.log('[EditorState doAutoSave] finalJson to be saved:', JSON.stringify(finalJson, null, 2));
    // *** KONIEC NOWEGO LOGU ***
    changesCountRef.current = 0;
  }

  // ============ Ręczny zapis =============
  function handleManualSave(customPages, options = {}) {
    if (publicMode && isAccepted) return Promise.reject(new Error("Nie można zapisać w trybie publicznym po akceptacji"));

    const usedPages = customPages || pages;
    // Głęboka kopia, aby nie modyfikować oryginalnego stanu `pages`
    const pagesToSave = JSON.parse(JSON.stringify(usedPages));

     // Konwertuj ścieżki SVG na URL przed zapisem
    pagesToSave.forEach(page => {
      if (page.svgs && Array.isArray(page.svgs)) {
        page.svgs = page.svgs.map(svg => {
          // Sprawdź, czy src wygląda jak lokalna ścieżka przed konwersją
          if (svg.src && typeof svg.src === 'string' && svg.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
             console.log(`[EditorState handleManualSave] Converting SVG path to URL: ${svg.src}`);
             const urlSrc = convertLocalPathToUrl(svg.src, backendUrl, publicMode);
             console.log(`[EditorState handleManualSave] Converted URL: ${urlSrc}`);
             return { ...svg, src: urlSrc };
          }
          return svg; // Zwróć bez zmian, jeśli src nie jest lokalną ścieżką
        });
      }
       // Podobnie dla obrazków, jeśli używają lokalnych ścieżek
       if (page.images && Array.isArray(page.images)) {
        page.images = page.images.map(img => {
          if (img.src && typeof img.src === 'string' && img.src.startsWith('/home/szymon/kreator2/backend/uploads/')) {
            console.log(`[EditorState handleManualSave] Converting Image path to URL: ${img.src}`);
            const urlSrc = convertLocalPathToUrl(img.src, backendUrl, publicMode);
            console.log(`[EditorState handleManualSave] Converted URL: ${urlSrc}`);
            return { ...img, src: urlSrc };
          }
          return img;
        });
      }
    });

    const final = pagesToSave.map((pg, idx) => ({
      ...pg,
      pageIndex: pg.pageIndex ?? idx
    }));
    const finalJson = { pages: final };
    if (downloadKey) finalJson.downloadKey = downloadKey;

    // Dodajemy log dla opcji
    console.log('[EditorState handleManualSave] Options:', options);

    // Zwracamy Promise, aby można było czekać na zakończenie zapisu
    return handleSaveToDb({
      projectId,
      downloadKey,
      jsonData: finalJson,
      saveType: "manual",
      backendUrl,
      isPublicMode: publicMode,
      publicMethodUsed,
      isTemplateEditor, // Przekaż flagę
      options // Przekazujemy opcje do handleSaveToDb
    }).catch((err) => { 
      console.error("Manual save failed:", err);
      throw err; // Przekazujemy błąd dalej
    });
    // *** NOWY LOG: Sprawdź zawartość svgs przed zapisem ***
    // console.log('[EditorState handleManualSave] finalJson to be saved:', JSON.stringify(finalJson, null, 2));
    // *** KONIEC NOWEGO LOGU ***
  }


  // Combined Effect for Initial Load (History or pdfData Fallback)
  useEffect(() => {
    // Zapobiegaj wielokrotnemu uruchamianiu, jeśli zależności się nie zmieniły znacząco
    // (szczególnie pdfData, które może być obiektem i zmieniać referencję)
    if (initialLoadEffectRan.current && !projectId && !downloadKey) {
        // Jeśli efekt już raz się wykonał i nie ma ID do załadowania, prawdopodobnie
        // zmiana pdfData nie powinna triggerować ponownego ładowania.
        // console.log("[EditorState] Initial load effect already ran, skipping potentially redundant run.");
        // return;
        // TODO: Dokładniej przemyśleć warunki ponownego uruchomienia. Na razie zostawiamy,
        // aby zmiany w projectId/downloadKey na pewno triggerowały.
    }

    console.log("[EditorState] Initial Load Effect Triggered", { projectId, downloadKey, isTemplateEditor, pdfDataPublicMode: pdfData?.publicMode });
    initialLoadEffectRan.current = true; // Oznacz, że efekt został uruchomiony

    // --- Determine current mode based on pdfData ---
    const isCurrentlyPublic = !!pdfData?.publicMode; // Ustal tryb dla TEGO wykonania hooka
    console.log(`[EditorState] Determined mode for this run: isCurrentlyPublic = ${isCurrentlyPublic}`);

    // --- Set publicMode state for future renders ---
    // Aktualizuj stan, aby inne części komponentu miały poprawną wartość w przyszłych renderowaniach
    setPublicMode(isCurrentlyPublic);


    // --- Process pdfData (only needed for potential fallback) ---
    let initialPagesFromPdfData = null;
    if (pdfData) {
        let pagesDataToParse = null;
        if (Array.isArray(pdfData?.pdfMetadatas) && pdfData.pdfMetadatas.length > 0 && pdfData.pdfMetadatas[0]?.metadata?.pages) {
            pagesDataToParse = pdfData.pdfMetadatas[0].metadata.pages;
        } else if (Array.isArray(pdfData?.pages) && pdfData.pages.length > 0) {
            pagesDataToParse = pdfData.pages;
        }
        if (pagesDataToParse) {
            initialPagesFromPdfData = pagesDataToParse.map((pg, i) => {
                if (pg.pageIndex === undefined) pg.pageIndex = i;
                return parseOnePage(pg); // Użyj parseOnePage do przetworzenia
            });
            console.log("[EditorState] Parsed initial pdfData for potential fallback.");
        } else {
             console.log("[EditorState] pdfData provided, but no valid pages structure found inside.");
        }
    } else {
         console.log("[EditorState] No initial pdfData provided.");
    }

    // --- History Loading Logic ---
    const loadHistory = async () => {
        const cleanedProjectId = projectId ? String(projectId).replace(/[^0-9]/g, '') : null;
        const cleanedDownloadKey = downloadKey ? String(downloadKey).replace(/[^a-zA-Z0-9]/g, '') : null;

        if (!cleanedProjectId && !cleanedDownloadKey) {
            console.log("[EditorState] No valid projectId or downloadKey for loading history.");
            return null;
        }

        if (window.preventProjectReload) {
            console.log("[EditorState] Skipping history load (preventProjectReload=true)");
            return null;
        }

        setLoadingPages(true);
        let loadedData = null;
        let done = false;
        let currentPublicMethodUsed = null; // Lokalna zmienna dla tego wywołania

        // a) Try loading via downloadKey (public)
        // Użyj zmiennej 'isCurrentlyPublic' zamiast stanu 'publicMode'
        if (isCurrentlyPublic && cleanedDownloadKey && !done) {
            const urlKey = `${backendUrl}/client/projects-key/${cleanedDownloadKey}/history/latest`;
            console.log(`[EditorState] Attempting load via key (isCurrentlyPublic=${isCurrentlyPublic}): ${urlKey}`);
            try {
                const r1 = await fetch(urlKey, { credentials: "include" });
                if (r1.ok) {
                    const d1 = await r1.json();
                    // Sprawdź, czy odpowiedź zawiera jsonData i czy jsonData.pages jest tablicą
                    if (d1 && d1.jsonData && Array.isArray(d1.jsonData.pages)) {
                        loadedData = d1; // Zapisz cały obiekt odpowiedzi
                        currentPublicMethodUsed = "key";
                        done = true;
                        console.log("[EditorState] Load via key successful.");
                    } else { console.log("[EditorState] Load via key OK, but no valid jsonData.pages array found."); }
                } else { console.log(`[EditorState] Load via key failed, status: ${r1.status}`); }
            } catch (err) { console.error("[EditorState] Load via key fetch error:", err); }
        }

        // b) Try loading via projectId (public)
        // Użyj zmiennej 'isCurrentlyPublic' zamiast stanu 'publicMode'
        if (!done && isCurrentlyPublic && cleanedProjectId) {
            const urlPublicId = `${backendUrl}/client/projects/${cleanedProjectId}/history/latest`;
            console.log(`[EditorState] Attempting load via public id (isCurrentlyPublic=${isCurrentlyPublic}): ${urlPublicId}`);
            try {
                const r2 = await fetch(urlPublicId, { credentials: "include" });
                if (r2.ok) {
                    const d2 = await r2.json();
                    if (d2 && d2.jsonData && Array.isArray(d2.jsonData.pages)) {
                        loadedData = d2; // Zapisz cały obiekt odpowiedzi
                        currentPublicMethodUsed = "id";
                        done = true;
                        console.log("[EditorState] Load via public id successful.");
                    } else { console.log("[EditorState] Load via public id OK, but no valid jsonData.pages array found."); }
                } else { console.log(`[EditorState] Load via public id failed, status: ${r2.status}`); }
            } catch (err) { console.error("[EditorState] Load via public id fetch error:", err); }
        }

        // c) Try loading via admin projectId (project or template)
        // Użyj zmiennej 'isCurrentlyPublic' zamiast stanu 'publicMode'
        if (!done && !isCurrentlyPublic && cleanedProjectId) {
            const endpointPath = isTemplateEditor ? `/admin/templates/${cleanedProjectId}/history/latest` : `/admin/projects/${cleanedProjectId}/history/latest`;
            const urlAdmin = `${backendUrl}${endpointPath}`;
            console.log(`[EditorState] Attempting load via admin id (isCurrentlyPublic=${isCurrentlyPublic}, isTemplateEditor=${isTemplateEditor}): ${urlAdmin}`);
            try {
                const r3 = await fetch(urlAdmin, { credentials: "include" });
                if (r3.ok) {
                    const d3 = await r3.json();
                    if (d3 && d3.jsonData && Array.isArray(d3.jsonData.pages)) {
                        loadedData = d3; // Zapisz cały obiekt odpowiedzi
                        currentPublicMethodUsed = null;
                        done = true;
                        console.log(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) successful.`);
                    } else { console.log(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) OK, but no valid jsonData.pages array found.`); }
                } else { console.log(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) failed, status: ${r3.status}`); }
            } catch (err) { console.error(`[EditorState] Load via admin id (${isTemplateEditor ? 'template' : 'project'}) fetch error:`, err); }
        }

        console.log("[EditorState] Fetching history completed.", { loadedData: !!loadedData, done });
        setLoadingPages(false);
        // Ustaw publicMethodUsed globalnie po zakończeniu prób
        setPublicMethodUsed(currentPublicMethodUsed);
        return loadedData; // Zwróć cały obiekt odpowiedzi lub null
    };

    // --- Wywołanie logiki ładowania ---
    loadHistory().then(historyData => {
        // historyData to teraz cały obiekt odpowiedzi z backendu lub null
        if (historyData && historyData.jsonData && Array.isArray(historyData.jsonData.pages)) {
            // Użyj danych z DB
            const parsedPagesFromDb = historyData.jsonData.pages.map(parseOnePage); // Dostęp do stron przez jsonData.pages
            const loadSource = publicMethodUsed ? `key/id ${publicMethodUsed}` : (isTemplateEditor ? 'template history' : 'project history');
            console.log(`[EditorState] Setting pages from DB (${loadSource}).`);
            setPages(parsedPagesFromDb);
            setHasLoadedFromDb(true);
            // Ustaw initialAutoCentered na podstawie danych z historii (zagnieżdżone w jsonData)
            console.log("[EditorState] Sprawdzanie flagi autoCentered. Klucze w historyData:", Object.keys(historyData));
            if (historyData.jsonData && historyData.jsonData.hasOwnProperty('autoCentered')) {
              console.log(`[EditorState] Ustawianie initialAutoCentered z historii (jsonData.autoCentered): ${historyData.jsonData.autoCentered}`);
              setInitialAutoCentered(!!historyData.jsonData.autoCentered);
            } else {
              console.log("[EditorState] Brak flagi autoCentered w jsonData historii, ustawianie initialAutoCentered na false.");
              setInitialAutoCentered(false);
            }
        } else {
            // Fallback do przetworzonych danych z pdfData (jeśli dostępne)
            if (initialPagesFromPdfData) {
                console.log("[EditorState] Setting pages from initial pdfData (fallback).");
                setPages(initialPagesFromPdfData);
            } else {
                console.log("[EditorState] No DB history and no valid pdfData fallback. Setting empty pages.");
                setPages([]); // Ustaw puste strony, jeśli nic nie ma
            }
            setHasLoadedFromDb(false); // Upewnij się, że jest false przy fallbacku
            setInitialAutoCentered(false); // Przy fallbacku zakładamy, że nie było centrowane
        }
    }).catch(error => {
        console.error("[EditorState] Error during history load/processing:", error);
        // Próba fallbacku nawet przy błędzie
        if (initialPagesFromPdfData) {
            console.log("[EditorState] Setting pages from initial pdfData (error fallback).");
            setPages(initialPagesFromPdfData);
        } else {
             console.log("[EditorState] No history and no pdfData fallback on error. Setting empty pages.");
            setPages([]);
        }
        setHasLoadedFromDb(false);
        setLoadingPages(false);
        setInitialAutoCentered(false); // Przy błędzie również zakładamy, że nie było centrowane
    });

  // Zaktualizowano zależności: usunięto publicMode (stan), dodano pdfData (bo od niego zależy isCurrentlyPublic)
  }, [projectId, downloadKey, backendUrl, pdfData, isTemplateEditor]);


  // --- USUNIĘTO EFEKT JEDNORAZOWEGO CENTROWANIA ---
  // Logika centrowania jest teraz obsługiwana w useEditorLocalCallbacks -> handleChangeTextLocal,
  // wyzwalana przez EditableText po zmierzeniu wymiarów.


  // 4) fonty (zmieniono numerację komentarza)
  useEffect(() => {
    let isMounted = true;
    (async function loadAllFonts() {
      try {
        const fontsUrl = publicMode
          ? `${backendUrl}/client/fonts`
          : `${backendUrl}/admin/fonts`;
        const resp = await fetch(fontsUrl, { credentials: "include" });
        if (!resp.ok) throw new Error("font error");
        const list = await resp.json();
        const prefixRaw = publicMode
          ? `${backendUrl}/client/raw-file?path=`
          : `${backendUrl}/admin/raw-file?path=`;

        const tasks = list.map(async (f) => {
          let pathF = f.filePath || "";
          const prefix = "/home/szymon/kreator2/backend/uploads/";
          if (pathF.startsWith(prefix)) {
            pathF = pathF.slice(prefix.length);
          }
          if (!pathF.includes("/")) {
            pathF = "fonts/" + pathF;
          }
          pathF = pathF.replace(/[()]/g, "").replace(/\s/g, "");
          const finalUrl = prefixRaw + encodeURIComponent(pathF);

          try {
            const ff = new FontFace(f.name, `url(${finalUrl})`);
            await ff.load();
            document.fonts.add(ff);
          } catch {}
        });
        await Promise.allSettled(tasks);
        if (isMounted) {
          setAvailableFonts(list);
          setFontsAreLoaded(true);
        }
      } catch {
        if (isMounted) setFontsAreLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [backendUrl, publicMode]);

  // useEffect(() => { // Ten useEffect może nie być już potrzebny, jeśli forceRefresh jest wyzwalany inaczej
  //   if (fontsAreLoaded) setForceRefresh((p) => p + 1);
  // }, [fontsAreLoaded]);

  // 5) assety (zmieniono numerację komentarza)
  useEffect(() => {
    if (pages.length === 0) {
      setAssetsAreLoaded(true);
      return;
    }
    let total = 0;
    let loaded = 0;
    pages.forEach((pg) => {
      total += (pg.images || []).length;
      total += (pg.svgs || []).length;
    });
    if (total === 0) {
      setAssetsAreLoaded(true);
      setAssetsCount(0);
      setAssetsTotal(0);
      return;
    }
    setAssetsTotal(total);
    setAssetsCount(0);

    pages.forEach((pg) => {
      (pg.images || []).forEach((img) => {
        const iObj = new Image();
        iObj.crossOrigin = "anonymous";
        iObj.onload = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        iObj.onerror = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        iObj.src = img.src || "";
      });
      (pg.svgs || []).forEach((sv) => {
        const sObj = new Image();
        sObj.crossOrigin = "anonymous";
        sObj.onload = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        sObj.onerror = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        sObj.src = sv.src || "";
      });
    });
    // Usunięto dodatkowe });
  }, [pages]);

  // Usunięto useEffect dla autosnapu - logika przeniesiona z powrotem do Editor.js

  // Obsługa guides:
  // Dodajemy zmienną do śledzenia ostatniego czasu dodania prowadnicy
  const lastGuideAddTimeRef = useRef(0);

  // Dodajemy licznik wywołań dla debugowania
  const addGuideCallCountRef = useRef(0);

  // Dodajemy referencję do ostatnio dodanej prowadnicy
  const lastAddedGuideRef = useRef({ timestamp: 0, id: null });

  function handleAddGuide({ orientation, expression }) {
    // Zwiększamy licznik wywołań
    const callCount = ++addGuideCallCountRef.current;
    console.log(`[EditorState] handleAddGuide wywołane (${callCount}): `, { orientation, expression });

    // Dodajemy stack trace, aby zobaczyć skąd jest wywoływana funkcja
    console.log(`[EditorState] Stack trace dla wywołania ${callCount}:`, new Error().stack);

    if (publicMode && isAccepted) {
      console.log(`[EditorState] Przerwano dodawanie prowadnicy (${callCount}) - tryb publiczny i zaakceptowane`);
      return;
    }

    // Sprawdzamy, czy nie próbujemy dodać prowadnicy zbyt szybko po poprzedniej
    const now = Date.now();
    if (now - lastGuideAddTimeRef.current < 500) {
      console.log(`[EditorState] Zablokowano dodanie prowadnicy (${callCount}) - zbyt szybko po poprzedniej (${now - lastGuideAddTimeRef.current}ms)`);
      return; // Blokujemy dodanie prowadnicy, jeśli minęło mniej niż 500ms
    }

    // Aktualizujemy czas ostatniego dodania prowadnicy
    lastGuideAddTimeRef.current = now;
    console.log(`[EditorState] Aktualizowano lastGuideAddTimeRef na ${now}`);

    // Generujemy unikalny identyfikator z większą precyzją
    const uniqueId = "guide-" + now + "-" + Math.floor(Math.random() * 1000);
    console.log(`[EditorState] Wygenerowano uniqueId: ${uniqueId}`);

    // Zapisujemy informacje o ostatnio dodanej prowadnicy
    lastAddedGuideRef.current = { timestamp: now, id: uniqueId };

    setPages((prev) => {
      console.log(`[EditorState] setPages wywołane dla prowadnicy (${callCount})`);

      // Sprawdzamy, czy ta sama aktualizacja nie jest wykonywana dwukrotnie
      // Porównujemy aktualną liczbę prowadnic z liczbą prowadnic w poprzednim stanie
      const currentGuides = prev[currentPage]?.guides || [];

      // Sprawdzamy, czy prowadnica o tym samym wyrażeniu i orientacji już istnieje
      const duplicateGuide = currentGuides.find(g =>
        g.orientation === orientation &&
        g.expression === expression &&
        // Sprawdzamy, czy prowadnica została dodana niedawno (w ciągu ostatnich 1000ms)
        g.id.startsWith(`guide-${Math.floor(now / 1000)}`)
      );

      if (duplicateGuide) {
        console.log(`[EditorState] Wykryto duplikat prowadnicy: ${duplicateGuide.id}, pomijam dodawanie`);
        return prev; // Zwracamy poprzedni stan bez zmian
      }

      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) {
        console.log(`[EditorState] Nieprawidłowy indeks strony: ${currentPage}`);
        return arr;
      }

      const pCopy = { ...arr[currentPage] };
      const existingGuides = pCopy.guides || []; // Pobierz istniejące lub utwórz pustą (ZMIENIONA NAZWA)

      // Zastąp .push() tworzeniem nowej tablicy
      pCopy.guides = [...existingGuides, { // Użyj zmienionej nazwy
        id: uniqueId,
        orientation,
        expression
      }];

      console.log(`[EditorState] Dodano prowadnicę (${callCount}), aktualna liczba prowadnic: ${pCopy.guides.length}`);
      console.log(`[EditorState] Lista prowadnic po dodaniu:`, pCopy.guides);

      arr[currentPage] = pCopy;
      return arr;
    });

    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      console.log(`[EditorState] Wykonuję doAutoSave() po dodaniu prowadnicy (${callCount})`);
      doAutoSave();
      changesCountRef.current = 0;
    }

    console.log(`[EditorState] Zakończono handleAddGuide (${callCount})`);
  }
  function handleRemoveGuide(guideId) {
    if (publicMode && isAccepted) return;
    setPages((prev) => {
      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) return arr;
      const pCopy = { ...arr[currentPage] };
      if (!pCopy.guides) pCopy.guides = [];
      pCopy.guides = pCopy.guides.filter((g) => g.id !== guideId);
      arr[currentPage] = pCopy;
      return arr;
    });
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
  }
  function handleChangeGuide(guideId, newExpression) {
    if (publicMode && isAccepted) return;
    setPages((prev) => {
      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) return arr;
      const pCopy = { ...arr[currentPage] };
      if (!pCopy.guides) pCopy.guides = [];
      pCopy.guides = pCopy.guides.map((g) =>
        g.id === guideId ? { ...g, expression: newExpression } : g
      );
      arr[currentPage] = pCopy;
      return arr;
    });
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
  }

  return {
    // podst. tablice
    pages,
    setPages,
    currentPage,
    setCurrentPage,

    // wczytywanie stron
    hasLoadedFromDb,
    loadingPages,

    // fonty, assets
    fontsAreLoaded,
    availableFonts,
    assetsAreLoaded,
    assetsTotal,
    assetsCount,

    // zaznaczenia
    selectedShapeId,
    setSelectedShapeId,
    selectedObject,
    setSelectedObject,
    panelMode,
    setPanelMode,
    editingTextId,
    setEditingTextId,

    forceRefresh,
    doAutoSave,
    handleManualSave,
    changesCountRef,

    // public vs admin
    publicMode,
    publicMethodUsed,

    // guides
    handleAddGuide,
    handleRemoveGuide,
    handleChangeGuide,

    // Funkcja do manualnego wyzwalania odświeżenia
    triggerForceRefresh: () => setForceRefresh(p => p + 1),
    // Usunięto zwracanie autoSnapAllPagesWithSimulation
    initialAutoCentered // Zwracamy nowy stan
  };
}
